import React, { useState } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  Tabs,
  PasswordInput,
  Text,
  CopyButton,
  ActionIcon,
  Tooltip,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { User } from "types";
import { useChangeUserPassword } from "api/users/changePassword";
import { useChangeUserPin } from "api/users/changePin";
import { Copy, Check } from "tabler-icons-react";

type ChangeCredentialsModalProps = {
  opened: boolean;
  onClose: () => void;
  member: User<string>;
};

function ChangeCredentialsModal({
  opened,
  onClose,
  member,
}: ChangeCredentialsModalProps) {
  const [tab, setTab] = useState<string | null>("password");
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const { mutate: changePassword, isLoading: isChangingPassword } =
    useChangeUserPassword();
  const { mutate: changePin, isLoading: isChangingPin } = useChangeUserPin();

  const passwordForm = useForm({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      newPassword: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      confirmPassword: (value, values) =>
        value !== values.newPassword ? "Passwords do not match" : null,
    },
  });

  const handlePasswordSubmit = passwordForm.onSubmit((values) => {
    changePassword(
      { userId: member._id, newPassword: values.newPassword },
      {
        onSuccess: (data) => {
          if (data.newPassword) {
            setGeneratedPassword(data.newPassword);
            passwordForm.reset();
          }
        },
      }
    );
  });

  const handleRegeneratePin = () => {
    changePin(
      { userId: member._id, newPin: "" },
      {
        onSuccess: (data) => {
          if (data.loginPin) {
            setGeneratedPin(data.loginPin);
          }
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Change Credentials for ${member.firstName} ${member.lastName}`}
      centered
      size="sm"
    >
      <Tabs value={tab} onTabChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="password">Change Password</Tabs.Tab>
          <Tabs.Tab value="pin">Reset PIN</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="password" pt="md">
          <Stack spacing="md">
            {!generatedPassword ? (
              <form onSubmit={handlePasswordSubmit}>
                <Stack spacing="md">
                  <Text size="sm" color="dimmed">
                    Set a new password for this user
                  </Text>
                  <PasswordInput
                    label="New Password"
                    placeholder="Enter new password"
                    required
                    {...passwordForm.getInputProps("newPassword")}
                  />
                  <PasswordInput
                    label="Confirm Password"
                    placeholder="Confirm new password"
                    required
                    {...passwordForm.getInputProps("confirmPassword")}
                  />
                  <Group position="right" mt="md">
                    <Button variant="default" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isChangingPassword}
                      disabled={isChangingPassword}
                    >
                      Change Password
                    </Button>
                  </Group>
                </Stack>
              </form>
            ) : (
              <>
                <Alert icon={<Check size={16} />} color="green" title="Password Changed Successfully">
                  <Text size="sm" weight={500} mb="xs">
                    New Password:
                  </Text>
                  <Group spacing="xs">
                    <Text
                      size="lg"
                      weight={700}
                      sx={{
                        fontFamily: "monospace",
                        padding: "8px 12px",
                        backgroundColor: "#c8e6c9",
                        borderRadius: "4px",
                        flex: 1,
                        wordBreak: "break-all",
                      }}
                    >
                      {generatedPassword}
                    </Text>
                    <CopyButton value={generatedPassword} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={copied ? "teal" : "gray"}
                            variant="light"
                            onClick={copy}
                          >
                            {copied ? (
                              <Check size={16} />
                            ) : (
                              <Copy size={16} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <Text size="xs" color="dimmed" mt="sm">
                    Share this new password with the user securely.
                    <br />
                    Make sure they change it on first login.
                  </Text>
                </Alert>
                <Group position="right" mt="md">
                  <Button
                    onClick={() => {
                      setGeneratedPassword(null);
                      passwordForm.reset();
                    }}
                    variant="default"
                  >
                    Set Another Password
                  </Button>
                  <Button onClick={onClose} color="green">
                    Done
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="pin" pt="md">
          <Stack spacing="md">
            <Text size="sm" color="dimmed">
              Generate a new auto-generated PIN for this user
            </Text>

            {member.loginPin && !generatedPin && (
              <Alert color="blue" title="Current PIN">
                <Text size="sm" weight={500} mb="xs">
                  Current PIN:
                </Text>
                <Group spacing="xs">
                  <Text
                    size="lg"
                    weight={700}
                    sx={{
                      fontFamily: "monospace",
                      padding: "8px 12px",
                      backgroundColor: "#e3f2fd",
                      borderRadius: "4px",
                      flex: 1,
                    }}
                  >
                    {member.loginPin}
                  </Text>
                  <CopyButton value={member.loginPin} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied" : "Copy"}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={copied ? "teal" : "gray"}
                          variant="light"
                          onClick={copy}
                        >
                          {copied ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              </Alert>
            )}

            {generatedPin && (
              <>
                {member.loginPin && (
                  <Alert color="blue" title="Previous PIN">
                    <Text size="sm" weight={500} mb="xs">
                      Previous PIN:
                    </Text>
                    <Text
                      size="lg"
                      weight={700}
                      sx={{
                        fontFamily: "monospace",
                        padding: "8px 12px",
                        backgroundColor: "#e3f2fd",
                        borderRadius: "4px",
                      }}
                    >
                      {member.loginPin}
                    </Text>
                  </Alert>
                )}
                <Alert icon={<Check size={16} />} color="green" title="New PIN Generated">
                  <Text size="sm" weight={500} mb="xs">
                    New PIN:
                  </Text>
                  <Group spacing="xs">
                    <Text
                      size="lg"
                      weight={700}
                      sx={{
                        fontFamily: "monospace",
                        padding: "8px 12px",
                        backgroundColor: "#c8e6c9",
                        borderRadius: "4px",
                        flex: 1,
                      }}
                    >
                      {generatedPin}
                    </Text>
                    <CopyButton value={generatedPin} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={copied ? "teal" : "gray"}
                            variant="light"
                            onClick={copy}
                          >
                            {copied ? (
                              <Check size={16} />
                            ) : (
                              <Copy size={16} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <Text size="xs" color="dimmed" mt="sm">
                    Share this new PIN with the user securely. Previous PIN is now inactive.
                  </Text>
                </Alert>
              </>
            )}

            {!generatedPin && (
              <Text size="sm" color="dimmed">
                Click the button below to generate a new PIN for this user
              </Text>
            )}

            <Group position="right" mt="md">
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
              <Button
                onClick={handleRegeneratePin}
                loading={isChangingPin}
                disabled={isChangingPin}
              >
                Generate PIN
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}

export default ChangeCredentialsModal;
